import * as P230Messages from "../misc/api/DeviceLinkApi/P230In2itiveMessages";
import { GetAlerts, GetCompliance, GetSession, GetSubject, isIn2itiveError, ClearMemory, RestartWithWait, ReConnectToDevice, GetHomeSpirometrySessions, GetHomeSpirometrySessionAnalysis, GetHomeSpirometryTestAnalysis, GetAudit, GetCalibrations } from '../misc/api/DeviceLinkApi/P230In2itive';
import { Device } from '../misc/api/DeviceLinkApi/Devices';
import { DiaryPackageDeployment, DiarySession, QueryOfDiaryTest, QueryOfQuestionnaireTestAnswer, QuestionnaireTestAnswer, WebPortalClient, SubjectCreation, QueryOfFvcTest, AlertCreation, QueryOfFvcAnalysisSetTest, DiaryComplianceCreation, DeviceAuditTrailCreation, ApiException, CalibrationCreation, Calibration } from "webportalclient";
import { DevicesAction, DevicesState, SetDeviceOverallUploadStatusAction, UploadStatus, SetUploadStatusAction, SetInteractionModalStepAction } from './DevicesNewReducer';
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { NIL } from 'uuid';
import { isEmpty } from "lodash";
import { AuditTrailActionEnum } from "../misc/api/ApiAuditActions";

interface UploadResult {
    status: UploadStatus;
    information?: string | undefined;
}

export class UploadDiarySessionsAsyncAction {
    public static async UploadSingle(client: WebPortalClient, currentStudy: string, device: Device, diarySessions: DiarySession[], currentDeviceSubject: SubjectCreation, onStatusChange: (message: string) => void, onOverallStatusChange: (status: UploadStatus, message?: string) => void): Promise<UploadStatus> {
        const CheckValidSubject = async (currentDeviceSubject: SubjectCreation, client: WebPortalClient): Promise<boolean> => {
            try {
                const subject = await client.getSubjects(undefined, `id eq ${currentDeviceSubject.id} and site/study/id eq ${currentStudy}`, undefined, "site($expand=study)");
                if (subject.value.length > 0)
                    return true;
                else
                    return false;
            } catch {
                onOverallStatusChange(UploadStatus.Failed, P230Messages.SubjectForAnotherStudyAllocatedOnDevice(currentDeviceSubject.number));
                return false;
            }
        }

        try {
            if (!await CheckValidSubject(currentDeviceSubject, client))
                throw new Error(P230Messages.SubjectForAnotherStudyAllocatedOnDevice(currentDeviceSubject.number));

            let firstErrorStatus: string = undefined;
            let uploadResult = UploadStatus.Uploading;

            await client.postAuditTrailEvent({
                summary: `The In2itive eDiary '${device.serial}' for Participant '${currentDeviceSubject.number}' has started a USB upload.`,
                auditAction: AuditTrailActionEnum.StartedUsbUploadIn2itiveDiary
            });

            const sessionUploadResult = await UploadDiarySessionsAsyncAction.uploadSessions(client, device.id, currentStudy, diarySessions, onStatusChange);
            uploadResult = sessionUploadResult?.status ? sessionUploadResult.status : uploadResult;
            firstErrorStatus = !firstErrorStatus ? sessionUploadResult?.information : firstErrorStatus;

            if (uploadResult === UploadStatus.Failed)
                return uploadResult;

            const homeSpirometryUploadResult = await UploadDiarySessionsAsyncAction.uploadHomeSpirometry(client, device.id, onStatusChange);
            uploadResult = homeSpirometryUploadResult?.status ? homeSpirometryUploadResult.status : uploadResult;
            firstErrorStatus = !firstErrorStatus ? homeSpirometryUploadResult?.information : firstErrorStatus;

            const alertUploadResult = await UploadDiarySessionsAsyncAction.uploadAlerts(client, device.id, onStatusChange);
            uploadResult = alertUploadResult?.status ? alertUploadResult.status : uploadResult;
            firstErrorStatus = !firstErrorStatus ? alertUploadResult?.information : firstErrorStatus;
            
            const complianceUploadResult = await UploadDiarySessionsAsyncAction.uploadCompliance(client, device.id, onStatusChange);
            uploadResult = complianceUploadResult?.status ? complianceUploadResult.status : uploadResult;
            firstErrorStatus = !firstErrorStatus ? complianceUploadResult?.information : firstErrorStatus;

            const auditUploadResult = await UploadDiarySessionsAsyncAction.uploadDeviceAudit(client, device.id, onStatusChange);
            uploadResult = auditUploadResult?.status ? auditUploadResult.status : uploadResult;
            firstErrorStatus = !firstErrorStatus ? auditUploadResult?.information : firstErrorStatus;

            const calibrationUploadResult = await UploadDiarySessionsAsyncAction.uploadCalibrations(client, device.id, onStatusChange);
            uploadResult = calibrationUploadResult?.status ? calibrationUploadResult.status : uploadResult;
            firstErrorStatus = !firstErrorStatus ? calibrationUploadResult?.information : firstErrorStatus;

            if (firstErrorStatus !== undefined)
                onStatusChange(firstErrorStatus);

            return uploadResult === UploadStatus.Uploading ? UploadStatus.Uploaded : uploadResult;
        } catch (err) {
            onStatusChange(err.message ? err.message : P230Messages.FailedToUploadMessage);
            onOverallStatusChange(UploadStatus.Failed, err.message ? err.message : P230Messages.FailedToUploadMessage);
            return UploadStatus.Failed;
        }
    }

    private static async uploadSessions(client: WebPortalClient, deviceId: string, currentStudy: string, diarySessions: DiarySession[], onStatusChange: (message: string) => void): Promise<UploadResult> {

        for (const session of diarySessions) {
            if (!isEmpty(session.name)) {
                const getStudyScheduleSessionItem = await client.getStudyScheduleSessions("sessionTypeId", `label eq '${session.name}' and studyScheduleVisit/studySchedule/studyId eq ${currentStudy}`);
                if (getStudyScheduleSessionItem.value.length === 0) {
                    const error = `Failed to upload session with name '${session.name}', does not match expected visit schedule.`;
                    onStatusChange(error);
                    return { status: UploadStatus.Failed, information: error };
                }

                if (getStudyScheduleSessionItem.value[0].sessionTypeId === SessionTypes.EDiary) {
                        onStatusChange(`Uploading session ${diarySessions.indexOf(session) + 1}  of ${diarySessions.length}`);
                        let existingSession;
                        try {
                            existingSession = await client.getDiarySession(session.id);
                        }
                        catch (ex) {
                            if ("status" in (ex as ApiException)) {
                                if ((ex as ApiException)?.status === 404) {
                                    existingSession = null;
                                } else {
                                    const error = `Failed to upload session '${session.id}'`;
                                    onStatusChange(error);
                                    return { status: UploadStatus.Failed, information: error };
                                }
                            } else {
                                const error = `Failed to upload session '${session.id}'`;
                                onStatusChange(error);
                                return { status: UploadStatus.Failed, information: error };
                            }
                        }

                        if (!existingSession) {
                            try {
                                await client.postDiarySession(session);
                            } catch {
                                const error = `Failed to upload session '${session.id}'`;
                                onStatusChange(error);
                                return { status: UploadStatus.Failed, information: error };
                            }
                        }
                    }

                    if (session.tests) {
                        for (const test of session.tests) {
                            let existingTest: QueryOfDiaryTest;
                            try {
                                existingTest = await client.getDiaryTests(undefined, `sessionId eq ${session.id} and index eq ${test.index}`);
                            }
                            catch {
                                const error = `Failed to upload test '${test.index}' for session '${session.id}'`;
                                onStatusChange(error);
                                return { status: UploadStatus.Failed, information: error };
                            }

                            if (existingTest.value.length === 0) {
                                try {
                                    await client.postDiaryTest(test);
                                } catch {
                                    const error = `Failed to upload test '${test.index}' for session '${session.id}'`;
                                    onStatusChange(error);
                                    return { status: UploadStatus.Failed, information: error };
                                }
                            }
                        }
                    }

                    if (session.questionnaires) {
                        for (const questionnaire of session.questionnaires) {
                            let existingQuestionnaire;

                            try {
                                existingQuestionnaire = await client.getQuestionnaireTest(questionnaire.id);
                            } catch (ex) {
                                if ("status" in (ex as ApiException)) {
                                    if ((ex as ApiException)?.status === 404) {
                                        existingQuestionnaire = null;
                                    } else {
                                        const error = `Failed to upload questionnaire for session '${session.id}'`;
                                        onStatusChange(error);
                                        return { status: UploadStatus.Failed, information: error };
                                    }
                                } else {
                                    const error = `Failed to upload questionnaire for session '${session.id}'`;
                                    onStatusChange(error);
                                    return { status: UploadStatus.Failed, information: error };

                                }
                            }

                            if (!existingQuestionnaire) {
                                try {
                                    const questionnaireMutation = { ...questionnaire, answers: undefined };
                                    existingQuestionnaire = await client.postQuestionnaireTest(questionnaireMutation)
                                } catch {
                                    const error = `Failed to upload questionnaire for session '${session.id}'`;
                                    onStatusChange(error);
                                    return { status: UploadStatus.Failed, information: error };
                                }
                            }
                            if (questionnaire.answers) {
                                for (const answer of questionnaire.answers) {
                                    let existingAnswer: QueryOfQuestionnaireTestAnswer;

                                    try {
                                        existingAnswer = await client.getQuestionnaireTestAnswers(undefined, `questionnaireTestId eq ${existingQuestionnaire.id} and questionIndex eq '${answer.questionIndex}'`)
                                    } catch {
                                        const error = `Failed to upload questionnaire answers for session '${session.id}'`;
                                        onStatusChange(error);
                                        return { status: UploadStatus.Failed, information: error };
                                    }

                                    if (existingAnswer.value.length === 0) {
                                        try {
                                            const answerMutation: QuestionnaireTestAnswer = { ...answer, questionnaireTestId: existingQuestionnaire.id };
                                            await client.postQuestionnaireTestAnswer(answerMutation);
                                        } catch {
                                            const error = `Failed to upload questionnaire answers for session '${session.id}'`;
                                            onStatusChange(error);
                                            return { status: UploadStatus.Failed, information: error };
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    private static async uploadAlerts(client: WebPortalClient, deviceId: string, onStatusChange: (message: string) => void): Promise<UploadResult> {
        const allAlerts: AlertCreation[] = [];
        onStatusChange(`Uploading alerts...`);
        const getAlerts = async (lastRecordId?: string): Promise<UploadStatus> => {
            const result = await GetAlerts(deviceId, lastRecordId);

            if (result === undefined)
                return UploadStatus.Uploaded;

            if (isIn2itiveError(result)) {
                onStatusChange(result.error.message);
                return UploadStatus.PartialFailed;
            }

            allAlerts.push(...result.alerts);

            if (result.alerts.length === 100) {
                const lastId = result.alerts[result.alerts.length - 1].id;
                return getAlerts(lastId);
            }
            return;
        }

        const status = await getAlerts();
        if (status !== undefined)
            return { status: status };

        for (const alert of allAlerts) {
            let existingAlertResult;
            try {
                existingAlertResult = (await client.getAlerts(undefined, `id eq ${alert.id}`)).value;
            }
            catch {
                onStatusChange(`Failed to upload alert '${alert.headline}' for session '${alert.triggeredBySessionId}'`);
                return { status: UploadStatus.Failed, information: `Failed to upload alert '${alert.headline}' for session '${alert.triggeredBySessionId}'` };
            }
            try {
                if (existingAlertResult.length === 0) {
                    await client.postAlert({
                        ...alert,
                        alertTemplateId: "Vitalograph.WebPortal.Modules.Alerts.In2itiveEDiaryCatchAllAlerts",
                    });
                }
            } catch {
                onStatusChange(`Failed to upload alert '${alert.headline}' for session '${alert.triggeredBySessionId}'`);
                return { status: UploadStatus.Failed, information: `Failed to upload alert '${alert.headline}' for session '${alert.triggeredBySessionId}'` };
            }
        }
    }

    private static async uploadCompliance(client: WebPortalClient, deviceId: string, onStatusChange: (message: string) => void): Promise<UploadResult> {
        const allCompliance: DiaryComplianceCreation[] = [];
        onStatusChange(`Uploading compliance information...`);
        const getCompliance = async (lastRecordId?: string): Promise<UploadStatus> => {
            const result = await GetCompliance(deviceId, lastRecordId);

            if (result === undefined)
                return UploadStatus.Uploaded;

            if (isIn2itiveError(result)) {
                onStatusChange(result.error.message);
                return UploadStatus.PartialFailed;
            }

            allCompliance.push(...result.compliances);

            if (result.compliances.length === 100) {
                const lastId = result.compliances[result.compliances.length - 1].id;
                return getCompliance(lastId);
            }
            return;
        }

        const status = await getCompliance(deviceId);
        if (status !== undefined)
            return { status: status };

        for (const compliance of allCompliance) {
            let existingComplianceResult;
            try {
                existingComplianceResult = (await client.getDiaryCompliances(undefined, `id eq ${compliance.sessionId} and eDiarySessionComplianceIdentifier eq ${compliance.eDiarySessionComplianceIdentifier} and complianceType ne 4`)).value;
            }
            catch {
                const error = `Failed to upload compliance information for session '${compliance.sessionId}'`;
                onStatusChange(error);
                return { status: UploadStatus.Failed, information: error };
            }

            try {
                if (existingComplianceResult.length === 0) {
                    await client.postDiaryCompliance(compliance);
                }
            } catch {
                const error = `Failed to upload compliance information for session '${compliance.sessionId}'`;
                onStatusChange(error);
                return { status: UploadStatus.Failed, information: error };
            }
        }
    }

    private static async uploadDeviceAudit(client: WebPortalClient, deviceId: string, onStatusChange: (message: string) => void): Promise<UploadResult> {
        const allResults: DeviceAuditTrailCreation[] = [];
        onStatusChange(`Uploading device audit trail...`);
        const getAuditEvents = async (lastRecordId?: string): Promise<UploadStatus> => {
            const result = await GetAudit(deviceId, lastRecordId);
            
            if (result === undefined)
                return UploadStatus.Uploaded;

            if (isIn2itiveError(result)) {
                onStatusChange(result.error.message);
                return UploadStatus.PartialFailed;
            }

            allResults.push(...result.auditEvents);

            if (result.auditEvents.length === 100) {
                const lastId = result.auditEvents[result.auditEvents.length - 1].id;
                return getAuditEvents(lastId);
            }
            return;
        }

        const status = await getAuditEvents();
        if (status !== undefined)
            return { status: status };

        for (const event of allResults) {
            let existingAuditEventResult;

            try {
                existingAuditEventResult = (await client.getDeviceAuditTrails(undefined, `id eq ${event.id}`)).value;
            } catch {
                const error = `Failed to upload device audit event '${event.id}'`;
                onStatusChange(error);
                return { status: UploadStatus.Failed, information: error };
            }

            try {
                if (existingAuditEventResult.length === 0) {
                    await client.postDeviceAuditTrail(event);
                }
            } catch {
                const error = `Failed to upload device audit event '${event.id}'`;
                onStatusChange(error);
                return { status: UploadStatus.Failed, information: error };
            }
        }
    }

    private static async uploadHomeSpirometry(client: WebPortalClient, deviceId: string, onStatusChange: (message: string) => void): Promise<UploadResult> {
        onStatusChange(`Uploading home spirometry sessions...`);

        const result = await GetHomeSpirometrySessions(deviceId);
        const analysisSets = await GetHomeSpirometrySessionAnalysis(deviceId);
        const analysisSetTests = await GetHomeSpirometryTestAnalysis(deviceId);

        if (result === undefined)
            return { status: UploadStatus.Uploaded }

        if (isIn2itiveError(result)) {
            onStatusChange(result.error.message);
            return { status: UploadStatus.PartialFailed, information: result.error.message };
        }
        for (const session of result.fvcSessions) {

            let existingSession;
            try {
                existingSession = await client.getFvcSession(session.id);
            } catch (ex) {
                if ("status" in (ex as ApiException)) {
                    if ((ex as ApiException)?.status === 404) {
                        existingSession = null;
                    } else {
                        const error = `Failed to upload home spirometry session '${session.id}'`;
                        onStatusChange(error);
                        return { status: UploadStatus.Failed, information: error };
                    }
                } else {
                    const error = `Failed to upload home spirometry session '${session.id}'`;
                    onStatusChange(error);
                    return { status: UploadStatus.Failed, information: error };
                }
            }

            if (!existingSession) {
                try {
                    await client.postFvcSession(session);
                } catch (ex) {
                    const error = `Failed to upload home spirometry session '${session.id}'`;
                    onStatusChange(error);
                    return { status: UploadStatus.Failed, information: error };
                }
            }

            const analysisSetFromDiary = analysisSets.fvcAnalysisSet.find(fa => fa.sessionId === session.id);

            let existingAnalysisSet;
            try {
                existingAnalysisSet = await client.getSpirometryAnalysisSet(analysisSetFromDiary.id);
            } catch (ex) {
                if ("status" in (ex as ApiException)) {
                    if ((ex as ApiException)?.status === 404) {
                        existingAnalysisSet = null;
                    } else {
                        const error = `Failed to home spirometry analysis set '${analysisSetFromDiary.id}'`;
                        onStatusChange(error);
                        return { status: UploadStatus.Failed, information: error };
                    }
                } else {
                    const error = `Failed to home spirometry analysis set '${analysisSetFromDiary.id}'`;
                    onStatusChange(error);
                    return { status: UploadStatus.Failed, information: error };
                }
            }

            if (!existingAnalysisSet) {
                try {
                    existingAnalysisSet = await client.postSpirometryAnalysisSet(analysisSetFromDiary);
                } catch {
                    const error = `Failed to upload home spirometry analysis set '${analysisSetFromDiary.id}'`;
                    onStatusChange(error);
                    return { status: UploadStatus.Failed, information: error };
                }
            }

            if (session.tests) {
                for (const test of session.tests) {
                    let existingTest: QueryOfFvcTest;
                    try {
                        existingTest = await client.getFvcTests(undefined, `sessionId eq ${session.id} and index eq ${test.index}`);
                    }
                    catch {
                        const error = `Failed to upload home spirometry test '${test.id}'`;
                        onStatusChange(error);
                        return { status: UploadStatus.Failed, information: error };
                    }

                    if (existingTest.value.length === 0) {
                        try {
                            await client.postFvcTest(test);
                        } catch {
                            const error = `Failed to upload home spirometry test '${test.id}'`;
                            onStatusChange(error);
                            return { status: UploadStatus.Failed, information: error };
                        }
                    }

                    let existingAnalysisSetTests: QueryOfFvcAnalysisSetTest;
                    try {
                        existingAnalysisSetTests = await client.getSpirometryAnalysisSetTests(undefined, `analysisSetId eq ${existingAnalysisSet.id} and testId eq ${test.id}`);
                    }
                    catch {
                        const error = `Failed to upload home spirometry analysis set test '${test.id}'`;
                        onStatusChange(error);
                        return { status: UploadStatus.Failed, information: error };
                    }

                    if (existingAnalysisSetTests.value.length === 0) {
                        try {
                            const analysisSetTestFromDiary = analysisSetTests.fvcAnalysisSetTest.find(fast => fast.analysisSetId === analysisSetFromDiary.id && fast.testId === test.id);
                            if (analysisSetTestFromDiary) {
                                await client.postSpirometryAnalysisSetTest(analysisSetTestFromDiary);
                            }
                        } catch (ex) {
                            const error = `Failed to upload home spirometry analysis set test '${test.id}'`;
                            onStatusChange(error);
                            return { status: UploadStatus.Failed, information: error };
                        }
                    }
                }
            }
        }
    }

    private static async uploadCalibrations(client: WebPortalClient, deviceId: string, onStatusChange: (message: string) => void): Promise<UploadResult> {
        const allCalibrations: CalibrationCreation[] = [];
        onStatusChange(`Uploading calibrations...`);
        const getCalibrations = async (lastRecordId?: string): Promise<UploadStatus> => {
            const result = await GetCalibrations(deviceId, lastRecordId);

            if (result === undefined)
                return UploadStatus.Uploaded;

            if (isIn2itiveError(result)) {
                onStatusChange(result.error.message);
                return UploadStatus.PartialFailed;
            }

            allCalibrations.push(...result.eDiaryCalibration);

            // 10 not 100 (which is standard) for calibrations
            if (result.eDiaryCalibration.length === 10) {
                const lastId = result.eDiaryCalibration[result.eDiaryCalibration.length - 1].id;
                return getCalibrations(lastId);
            }
            return;
        }

        const status = await getCalibrations();
        if (status !== undefined)
            return { status: status };

        for (const calibration of allCalibrations) {
            let existingCalibrationResult: Calibration[];
            try {
                existingCalibrationResult = (await client.getCalibrations(undefined, `id eq ${calibration.id}`)).value;
            }
            catch {
                onStatusChange(`Failed to upload calibration '${calibration.id}'`);
                return { status: UploadStatus.Failed, information: `Failed to upload calibration '${calibration.id}'` };
            }
            try {
                if (existingCalibrationResult.length === 0) {
                    await client.postCalibration(calibration);
                }
            } catch {
                onStatusChange(`Failed to upload calibration '${calibration.id}'`);
                return { status: UploadStatus.Failed, information: `Failed to upload calibration '${calibration.id}'` };
            }
        }
    }

    private static async resetDeviceOnCompletion(deviceId: string, onStatusChange: (message: string) => void) {
        onStatusChange(P230Messages.ClearingDevice);
        const clearResult = await ClearMemory(deviceId);
        if (!clearResult) {
            return onStatusChange(P230Messages.FailedToClear);
        }

        await RestartWithWait(deviceId);

        await ReConnectToDevice();
    }

    public static async Upload(client: WebPortalClient, dispatch: React.Dispatch<DevicesAction>, state: DevicesState, currentStudyId: string) {
        const allUploadResults: UploadStatus[] = [];
        let allSessions = [];

        for (const device of state.selectedDevices) {
            let uploadResult = UploadStatus.Uploading;
            dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Uploading));
            dispatch(SetUploadStatusAction.create(P230Messages.CheckingForData));

            const getSessions = async (lastRecordId?: string): Promise<UploadStatus> => {
                const result = await GetSession(device.id);

                if (result === undefined)
                    return UploadStatus.Uploaded;

                if (isIn2itiveError(result)) {

                    return UploadStatus.PartialFailed;
                }

                allSessions.push(...result.eDiarySessions);

                if (result.eDiarySessions.length === 100) {
                    const lastId = result.eDiarySessions[result.eDiarySessions.length - 1].id;
                    return getSessions(lastId);
                }
                return;
            }

            const status = await getSessions(device.id);
            if (status === UploadStatus.PartialFailed) {
                uploadResult = UploadStatus.Failed;
                dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Failed, P230Messages.FailedToRetrieveSession));
            }   

            // HACK: For eDiary version 1.05.0040
            allSessions = allSessions.filter(x => x.id !== NIL);
            // ENDHACK

            const diarySessions: DiarySession[] = allSessions.map(s => {
                return {
                    id: s.id,
                    visitId: s.visitId,
                    name: s.name,
                    occurred: s.occurred,
                    batteryLevel: s.batteryLevel,
                    applicationVersion: s.applicationVersion,
                    serialNumber: s.serialNumber,
                    pefRepeatable: s.pefRepeatable,
                    pefRepeatability: s.pefRepeatability,
                    tests: s.tests ? s.tests.map(t => {
                        return {
                            id: undefined,
                            sessionId: s.id,
                            occurred: t.occurred,
                            index: t.index,
                            accepted: t.accepted,
                            qualityIndicators: t.qualityIndicators,
                            pef: t.pef,
                            fev1: t.fev1
                        }
                    }) : undefined,
                    questionnaires: s.questionnaires ? s.questionnaires.map(q => {
                        return {
                            id: q.id,
                            sessionId: s.id,
                            questionnaireId: q.questionnaireId,
                            score: q.score,
                            occurred: q.occurred,
                            name: q.name,
                            answers: q.answers ? q.answers.map(a => {
                                return {
                                    id: undefined,
                                    questionnaireTestId: q.id,
                                    questionIndex: a.questionIndex.toString(),
                                    answerIndex: a.answerIndex.toString(),
                                    answerWeight: a.answerWeight,
                                    answer: a.answer,
                                }
                            }) : undefined
                        }
                    }) : undefined
                } as DiarySession;
            });
            const distinctSessionNames = Array.from(new Set(allSessions.map(session => session.name)));
            const concatenatedSessionNamesString = distinctSessionNames.map(name => `'${encodeURIComponent(name)}'`).join(',');
            let resetOnCompletion = false;
            for (const id of state.upload.selectedSessionIds) {
                const visit = state.upload.parentVisitCandidates.find(v => v.id === id);

                const studyScheduleSessions = await client.getStudyScheduleSessions("id,resetDiaryOnUpload", `label in (${concatenatedSessionNamesString}) and studyScheduleVisit/studySchedule/studyId eq ${currentStudyId}`);

                if (studyScheduleSessions.value.some(ss => ss.resetDiaryOnUpload)) {
                    resetOnCompletion = true;
                }

                const subjectResult = await GetSubject(device.id);

                if (!subjectResult) {
                    uploadResult = UploadStatus.Failed;
                    dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Failed, P230Messages.NoSubjectOnDevice));
                }

                if (isIn2itiveError(subjectResult)) {
                    uploadResult = UploadStatus.Failed;
                    dispatch(SetDeviceOverallUploadStatusAction.create(device.id, UploadStatus.Failed, P230Messages.FailedToUploadMessage));
                }

                if (subjectResult && !isIn2itiveError(subjectResult)) {
                    uploadResult = await this.UploadSingle(
                        client,
                        currentStudyId,
                        device,
                        diarySessions,
                        subjectResult.subject,
                        (message) => dispatch(SetUploadStatusAction.create(message)),
                        (status, message) => dispatch(SetDeviceOverallUploadStatusAction.create(device.id, status, message))
                    );

                    await client.postAuditTrailEvent({
                        summary: `The In2itive eDiary '${device.serial}' for Participant '${subjectResult.subject.number}' has completed a USB upload with status '${UploadStatus[uploadResult]}'.`,
                        auditAction: AuditTrailActionEnum.CompletedUsbUploadIn2itiveDiary
                    });
                }
            }

            if (resetOnCompletion) {
                await UploadDiarySessionsAsyncAction.resetDeviceOnCompletion(device.id, (message) => dispatch(SetUploadStatusAction.create(message)));
            }

            allUploadResults.push(uploadResult);
            dispatch(SetDeviceOverallUploadStatusAction.create(device.id, uploadResult));
        }


        if (allUploadResults.some(r => r === UploadStatus.Failed)) {
            dispatch(SetInteractionModalStepAction.create(5));
        } else if (allUploadResults.some(r => r === UploadStatus.PartialFailed)) {
            dispatch(SetInteractionModalStepAction.create(5));
        } else {
            dispatch(SetUploadStatusAction.create(P230Messages.UploadComplete));
            dispatch(SetInteractionModalStepAction.create(4));
        }
    }
}
